import React, { useEffect, useState } from 'react';
import { Mail, MessageCircle, FileText, Video, CheckCircle2, Mic } from 'lucide-react';
import { THEME } from '../../theme';

const HowItWorksDiagram = ({ darkMode }) => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setStep((prev) => (prev + 1) % 5); // Increased steps for conversation
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  const sources = [
    { icon: Mail, label: 'Email', color: '#EA4335', delay: 0 },
    { image: '/slack.png', label: 'Slack', color: '#4A154B', delay: 0.2 },
    { image: 'https://upload.wikimedia.org/wikipedia/commons/4/45/Notion_app_logo.png', label: 'Notion', color: '#000000', delay: 0.4 }
  ];

  return (
    <div className="w-full h-auto md:h-[450px] relative flex items-center justify-center select-none overflow-hidden py-10 md:py-0">
      {/* Background Glow */}
      <div className={`absolute inset-0 bg-gradient-to-r from-teal-500/5 to-cyan-500/5 rounded-full blur-3xl animate-pulse`} />

      <div className="relative w-full max-w-4xl h-full flex flex-col md:flex-row items-center justify-between px-4 md:px-12 gap-8 md:gap-0">
        
        {/* Left Side: Sources */}
        <div className="flex flex-row md:flex-col gap-4 md:gap-8 z-10 w-full md:w-1/3 justify-center md:justify-start">
          {sources.map((source, index) => (
            <div 
              key={index}
              className={`relative flex items-center gap-4 p-3 rounded-xl border transition-all duration-500 ${
                step >= 0 
                  ? 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-lg translate-x-0 opacity-100' 
                  : 'bg-slate-50 dark:bg-slate-900 border-transparent shadow-none -translate-x-4 opacity-50'
              }`}
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 overflow-hidden" style={{ backgroundColor: source.color + '20' }}>
                {source.image ? (
                  <img src={source.image} alt={source.label} className="w-6 h-6 object-contain" />
                ) : (
                  <source.icon size={20} style={{ color: source.color }} />
                )}
              </div>
              <div className="hidden md:flex flex-col">
                 <span className="font-bold text-slate-700 dark:text-slate-200 text-sm">{source.label}</span>
                 {/* Conversation Bubbles */}
                 <div className="h-4 relative w-24">
                    <div className={`absolute left-0 top-0 text-[10px] bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full transition-all duration-300 ${step === 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                       New Request
                    </div>
                    <div className={`absolute left-0 top-0 text-[10px] bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 px-2 py-0.5 rounded-full transition-all duration-300 ${step === 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                       Bot Replying...
                    </div>
                 </div>
              </div>
            </div>
          ))}
        </div>

        {/* Center: The Bot Brain */}
        <div className="relative z-20 flex flex-col items-center justify-center w-full md:w-1/3 my-4 md:my-0">
           {/* Speaking/Active Glow Effect */}
           <div className={`absolute inset-0 bg-teal-400/30 rounded-full blur-2xl transition-all duration-500 ${
              step === 2 || step === 3 ? 'scale-150 opacity-100' : 'scale-50 opacity-0'
           }`} />
           
           <div className={`w-36 h-36 rounded-full bg-white dark:bg-slate-800 border-4 border-teal-500 flex items-center justify-center shadow-2xl relative z-10 transition-all duration-500 ${
             step === 2 ? 'scale-110 ring-8 ring-teal-500/20' : 'scale-100'
           }`}>
              <div className="relative w-full h-full flex items-center justify-center">
                 {/* Speaking Waves */}
                 {step === 2 && (
                    <>
                      <div className="absolute inset-0 rounded-full border-2 border-teal-500 animate-ping opacity-20" />
                      <div className="absolute inset-0 rounded-full border-2 border-teal-500 animate-ping opacity-20 delay-150" />
                    </>
                 )}
                 <img src="/hicapy.png" alt="Bot" className="w-24 h-24 object-contain relative z-10" />
              </div>
           </div>
           
           <div className="mt-6 h-8 flex items-center justify-center">
             <div className={`px-4 py-1 rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 text-xs font-bold uppercase tracking-wider transition-all duration-300 ${
               step === 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 hidden'
             }`}>
               Analyzing
             </div>
             <div className={`px-4 py-1 rounded-full bg-teal-600 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-all duration-300 ${
               step === 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 hidden'
             }`}>
               <Mic size={12} className="animate-pulse" /> Speaking
             </div>
             <div className={`px-4 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-bold uppercase tracking-wider transition-all duration-300 ${
               step >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 hidden'
             }`}>
               Executing
             </div>
           </div>
        </div>

        {/* Right Side: Action (Meeting) */}
        <div className="w-full md:w-1/3 flex justify-center md:justify-end">
            <div className={`relative z-10 p-6 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-xl transition-all duration-700 transform w-64 ${
            step >= 3 ? 'translate-y-0 md:translate-y-0 md:translate-x-0 opacity-100' : 'translate-y-8 md:translate-y-0 md:translate-x-8 opacity-50'
            }`}>
            {/* Arrow Pointer: Top on mobile, Left on desktop */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 md:left-auto md:-left-3 md:top-1/2 md:translate-x-0 md:-translate-y-1/2 w-6 h-6 bg-white dark:bg-slate-800 border-l border-t border-slate-200 dark:border-slate-700 transform rotate-45 md:-rotate-45" />
            
            <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0">
                    <Video size={24} />
                </div>
                <div>
                    <h4 className="font-bold text-slate-900 dark:text-white text-sm">Joining Meeting</h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Google Meet • Now</p>
                </div>
            </div>
            
            <div className="space-y-3 pl-1">
                <div className={`flex items-center gap-3 text-xs text-slate-600 dark:text-slate-300 transition-all duration-500 delay-100 ${step >= 3 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`}>
                    <CheckCircle2 size={16} className="text-teal-500" />
                    <span>Recording started</span>
                </div>
                <div className={`flex items-center gap-3 text-xs text-slate-600 dark:text-slate-300 transition-all duration-500 delay-300 ${step >= 3 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`}>
                    <CheckCircle2 size={16} className="text-teal-500" />
                    <span>Taking notes</span>
                </div>
                <div className={`flex items-center gap-3 text-xs text-slate-600 dark:text-slate-300 transition-all duration-500 delay-500 ${step >= 3 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`}>
                    <CheckCircle2 size={16} className="text-teal-500" />
                    <span>Representing you</span>
                </div>
            </div>
            </div>
        </div>

      </div>
      
      {/* Connecting Arrows (SVG Overlay) - Mobile Only */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none z-0 md:hidden" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ opacity: 0.3 }}>
         {/* Incoming Lines (Top to Center) - Adjusted X coordinates for tighter grouping */}
         <path d="M 32 15 C 32 35, 50 35, 50 45" fill="none" stroke={THEME.primary} strokeWidth="0.5" strokeDasharray="2,2" className={step === 1 ? 'animate-dash' : 'opacity-10'} />
         <path d="M 50 15 L 50 45" fill="none" stroke={THEME.primary} strokeWidth="0.5" strokeDasharray="2,2" className={step === 1 ? 'animate-dash' : 'opacity-10'} />
         <path d="M 68 15 C 68 35, 50 35, 50 45" fill="none" stroke={THEME.primary} strokeWidth="0.5" strokeDasharray="2,2" className={step === 1 ? 'animate-dash' : 'opacity-10'} />
         
         {/* Outgoing Reply Lines */}
         <path d="M 50 45 C 50 35, 32 35, 32 15" fill="none" stroke={THEME.secondary} strokeWidth="0.5" strokeDasharray="2,2" className={step === 2 ? 'animate-dash-reverse' : 'opacity-0'} />
         <path d="M 50 45 L 50 15" fill="none" stroke={THEME.secondary} strokeWidth="0.5" strokeDasharray="2,2" className={step === 2 ? 'animate-dash-reverse' : 'opacity-0'} />
         <path d="M 50 45 C 50 35, 68 35, 68 15" fill="none" stroke={THEME.secondary} strokeWidth="0.5" strokeDasharray="2,2" className={step === 2 ? 'animate-dash-reverse' : 'opacity-0'} />

         {/* Action Line */}
         <path d="M 50 55 L 50 80" fill="none" stroke={THEME.secondary} strokeWidth="1" className={step >= 3 ? 'animate-dash' : 'opacity-0'} />
      </svg>

      {/* Connecting Arrows (SVG Overlay) - Desktop Only */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none z-0 hidden md:block" style={{ opacity: 0.3 }}>
         {/* Incoming Lines */}
         <path d="M 250 100 C 350 100, 350 225, 450 225" fill="none" stroke={THEME.primary} strokeWidth="2" strokeDasharray="5,5" className={step === 1 ? 'animate-dash' : 'opacity-10'} />
         <path d="M 250 225 C 350 225, 350 225, 450 225" fill="none" stroke={THEME.primary} strokeWidth="2" strokeDasharray="5,5" className={step === 1 ? 'animate-dash' : 'opacity-10'} />
         <path d="M 250 350 C 350 350, 350 225, 450 225" fill="none" stroke={THEME.primary} strokeWidth="2" strokeDasharray="5,5" className={step === 1 ? 'animate-dash' : 'opacity-10'} />
         
         {/* Outgoing Reply Lines */}
         <path d="M 450 225 C 350 225, 350 100, 250 100" fill="none" stroke={THEME.secondary} strokeWidth="2" strokeDasharray="5,5" className={step === 2 ? 'animate-dash-reverse' : 'opacity-0'} />
         <path d="M 450 225 C 350 225, 350 225, 250 225" fill="none" stroke={THEME.secondary} strokeWidth="2" strokeDasharray="5,5" className={step === 2 ? 'animate-dash-reverse' : 'opacity-0'} />
         <path d="M 450 225 C 350 225, 350 350, 250 350" fill="none" stroke={THEME.secondary} strokeWidth="2" strokeDasharray="5,5" className={step === 2 ? 'animate-dash-reverse' : 'opacity-0'} />

         {/* Action Line */}
         <path d="M 550 225 L 700 225" fill="none" stroke={THEME.secondary} strokeWidth="3" className={step >= 3 ? 'animate-dash' : 'opacity-0'} />
      </svg>
    </div>
  );
};

export default HowItWorksDiagram;